import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { FluentProvider } from '@fluentui/react-components';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { msalConfig } from './authConfig';
import { legoTheme } from './theme/legoTheme';
import { AuthProvider } from './context/AuthContext';
import App from './App.jsx';

const msalInstance = new PublicClientApplication(msalConfig);

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// 初始化 MSAL 实例
async function initializeMsal() {
  try {
    await msalInstance.initialize();
    
    // 处理重定向返回的情况
    const tokenResponse = await msalInstance.handleRedirectPromise();
    if (tokenResponse) {
      console.log('Login redirect successful!');
      if (tokenResponse.account) {
        msalInstance.setActiveAccount(tokenResponse.account);
      }
      // 如果 state 中有原页面路径，登录后跳转回去
      const redirectPath = tokenResponse.state;
      if (redirectPath && /^\/[^/]/.test(redirectPath)) {
        console.log('[App] Restoring page after login:', redirectPath);
        window.location.replace(redirectPath);
        return;
      }
    }

    // Restore the account persisted by MSAL before React renders. Without an
    // active account, a newly opened tab can look signed out even though the
    // account and tokens are already available in localStorage.
    if (!msalInstance.getActiveAccount()) {
      const cachedAccounts = msalInstance.getAllAccounts();
      if (cachedAccounts.length > 0) {
        msalInstance.setActiveAccount(cachedAccounts[0]);
      }
    }
    
    // 渲染应用
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <MsalProvider instance={msalInstance}>
              <AuthProvider>
                <FluentProvider theme={legoTheme}>
                  <App />
                </FluentProvider>
              </AuthProvider>
            </MsalProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('MSAL initialization error:', error);
  }
}

// 启动应用
initializeMsal();
